import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { login, fetchCurrentUser } from '../lib/api';
import Input from '../components/Common/Input';
import Label from '../components/Common/Label';
import Button from '../components/Common/Button';
import PasswordInput from '../components/Common/PasswordInput';
import loginBanner from '../assets/images/loginBanner.png'
import logo from '../assets/images/nexusLogo.png';
import logoInverse from '../assets/images/GradientNEXUS_inverse.svg'
import CurrentUserContext from "../contexts/CurrentUserContext";
import OpenWeatherMapContext from "../contexts/OpenWeatherMapContext";
import { resolvePostAuthRedirectPath } from "../utils/redirect.js"

const LoginPage = () => {
    const [form, setForm] = useState({ email: '', password: '' });
    const [errors, setErrors] = useState({});
    const {login: contextLogin} =  useContext(CurrentUserContext);
    const {setApiKeys: setOpenWeatherMapApiKeys} = useContext(OpenWeatherMapContext);

    const location = useLocation();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(form.email)) {
            newErrors.email = 'Enter a valid email';
        }

        if (!form.password) {
            newErrors.password = 'Password is required'
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            const res = await login(form);

            if (res?.data?.code === 400) {
                setErrors((prev) => ({
                    ...prev,
                    password: res?.data?.message || 'Invalid credentials'
                }));
                return;
            }

            if (res?.status === 403 && (
                res?.data?.errorCode === "NOT_A_ZONE_MANAGEMENT_USER" ||
                `${res?.data?.message || ""}`.toLowerCase().includes("not a zone management user")
            )) {
                setErrors((prev) => ({
                    ...prev,
                    password: "Not a zone management user."
                }));
                return;
            }

            if (res?.status === 200) {
                const currentUserResponse = await fetchCurrentUser();
                const authenticatedPayload = currentUserResponse?.status === 200
                  ? currentUserResponse.data
                  : res.data?.data;
                const authenticatedUser = authenticatedPayload?.user || res.data?.data?.user;
                contextLogin(
                  authenticatedUser,
                  authenticatedPayload?.notification || null,
                );
                setOpenWeatherMapApiKeys(
                  authenticatedPayload?.frontendSecrets?.openWeatherMapApiKey || null,
                  authenticatedPayload?.frontendSecrets?.openWeatherMapCityIdApiKey || null,
                );

                const redirectPath = await resolvePostAuthRedirectPath(
                  authenticatedUser,
                  location.state?.from,
                );

                navigate(redirectPath, { replace: true });
            }

            setErrors((prev) => ({
                ...prev,
                password: res?.data?.message || "Login failed. Please try again."
            }));
        } catch (err) {
            console.error('Login error:', err);
            setErrors((prev) => ({
                ...prev,
                password: "Network error. Please try again."
            }));
        }
    };


    const handleForgotPassword = () => {
        navigate('/forgotpassword');
    }

    return (
        <>
            <div className="grow loginHolder !h-full w-full flex gap-10 bg-white landscape:h-auto md:landscape:h-full landscape:overflow-auto">
                <div className="flex w-full">
                    <div className="loginFormSection basis-full h-screen sm:basis-3/4 md:basis-3/5 mx-auto min-[2200px]:basis:[440px] lg:basis-[440px] flex justify-center items-center !py-0 lg:p-16 p-10 bg-white dark:bg-darkThemeBg relative w-full">
                        <div className="w-full">
                            <div className='loginLogoHolder'>
                                <img src={logo} alt="Logo" className="mx-auto w-40 dark:hidden block" />
                                <img src={logoInverse} alt="Logo" className="mx-auto w-40 dark:block hidden" />
                            </div>
                            <form className="loginForm mt-12" onSubmit={handleSubmit}>
                                <div className="space-y-5 sm:space-y-6">
                                    <div className="emailSection gap-1 flex flex-col relative">
                                        <div className="flex flex-col gap-1">
                                            <Label
                                                htmlFor="email"
                                                className="emailLabel text-dark-grey dark:text-white font-normal text-base leading-5 tracking-[-0.24px]"
                                            >
                                                Email
                                            </Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="text"
                                                data-testid="login-email-input"
                                                placeholder="Enter Email"
                                                onChange={handleChange}
                                                className="text-lg leading-6 py-2.5 px-3 rounded-md border border-[#AFC3BC] text-dark-grey dark:text-white h-11 bg-white dark:darkThemeGradiantBg dark:border-none dark:placeholder:text-white/40 placeholder:text-dark-grey/40 font-medium tracking-[-0.408px] outline-none"
                                            />
                                        </div>
                                        {errors.email && (
                                            <p
                                                className='text-sm text-critical font-normal leading-5 absolute -bottom-5'
                                                data-testid="login-email-error"
                                            >
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                    <PasswordInput
                                        name="password"
                                        value={form.password}
                                        onChange={handleChange}
                                        error={errors.password}
                                    />
                                    <div className="flex items-center flex-wrap gap-2 gap-y-3 justify-end">
                                        <Button
                                            className="text-theme underline leading-5 font-medium text-base tracking-[-0.24px] cursor-pointer"
                                            type="button"
                                            data-testid="forgot-password-button"
                                            onClick={handleForgotPassword}
                                        >
                                            Forgot Password?
                                        </Button>
                                    </div>
                                    <Button
                                        type="submit"
                                        data-testid="login-submit-button"
                                        className="loginBtn w-full bg-theme hover:bg-theme/80 h-10 font-bold leading-5 text-base justify-center items-center rounded-md text-white text-center flex !mt-12 cursor-pointer px-y py-3">
                                        Login
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="mediaColumn lg:flex grow hidden overflow-hidden h-screen">
                        <img src={loginBanner} alt='banner' className="h-full object-cover w-full" />
                    </div>
                </div>
                <div className="closeIconHolder absolute left-10 md:right-14 md:left-auto top-6 cursor-pointer sm:landscape:right-14 hidden md:hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M4.7475 20.1575C4.5575 20.1575 4.3675 20.0875 4.2175 19.9375C3.9275 19.6475 3.9275 19.1675 4.2175 18.8775L17.8775 4.2175C18.1675 3.9275 18.6475 3.9275 18.9375 4.2175C19.2275 4.5075 19.2275 4.9875 18.9375 5.2775L5.2775 19.9375C5.1375 20.0875 4.9375 20.1575 4.7475 20.1575Z"
                            fill="#717375" stroke="#717375"></path>
                        <path
                            d="M18.4075 20.1575C18.2175 20.1575 18.0275 20.0875 17.8775 19.9375L4.2175 5.2775C3.9275 4.9875 3.9275 4.5075 4.2175 4.2175C4.5075 3.9275 4.9875 3.9275 5.2775 4.2175L18.9375 18.8775C19.2275 19.1675 19.2275 19.6475 18.9375 19.9375C18.7875 20.0875 18.5975 20.1575 18.4075 20.1575Z"
                            fill="#717375" stroke="#717375"></path>
                    </svg>
                </div>
            </div>
        </>
    );
};

export default LoginPage;
